import {
    Autocomplete,
    Box,
    Card,
    CardHeader,
    Chip,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { RemoveCircleOutline, FilterAltOff, Search, AddCircleOutline } from '@mui/icons-material'
import CachedIcon from '@mui/icons-material/Cached'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import { WrapperTable } from '../components'
import { HttpLCLSRelacion, HttpClienteTipo, HttpClienteSubTipo } from './api'
import { HttpSectorComercialABM } from '../sectores-comerciales/api'
import { RelacionViewDTO, RelacionFields, RelacionItemDTO, ClienteTipoDTO, ClienteSubTipoDTO, ClienteTipoFields, ClienteSubTipoFields } from './types'
import { Controller, useForm } from 'react-hook-form'

type FilterForm = {
    codPais: any
    codSectorComercial: any
}

function ConfigRelaciones() {
    const { paisLogos } = usePaisLogo()
    const [relaciones, setRelaciones] = useState<RelacionViewDTO[]>([])
    const [sectores, setSectores] = useState<{ id: number; descripcion: string }[]>([])
    const [tipos, setTipos] = useState<ClienteTipoDTO[]>([])
    const [subtipos, setSubtipos] = useState<ClienteSubTipoDTO[]>([])

    // Filtros para agregar nueva relación
    const [nuevoTipo, setNuevoTipo] = useState<ClienteTipoDTO | null>(null)
    const [nuevoSubtipo, setNuevoSubtipo] = useState<ClienteSubTipoDTO | null>(null)

    const { control, watch, setValue } = useForm<FilterForm>({
        defaultValues: { codPais: null, codSectorComercial: null }
    })

    const selectedPais = watch('codPais')
    const selectedSector = watch('codSectorComercial')

    // Cargar tipos y subtipos
    useEffect(() => {
        HttpClienteTipo.getAll().then(setTipos)
        HttpClienteSubTipo.getAll().then(setSubtipos)
    }, [])

    // Cargar sectores cuando cambia el país
    useEffect(() => {
        if (!selectedPais) {
            setSectores([])
            setValue('codSectorComercial', null)
            return
        }
        HttpSectorComercialABM.getSectoresComerciales().then(data => {
            const filtered = data
                .filter(s => s.codPais === (selectedPais?.id ?? selectedPais))
                .map(s => ({ id: s.id, descripcion: `${s.nombreUnidadNegocio ?? ''} - ${s.nombreSectorComercial}` }))
                .sort((a, b) => a.descripcion.localeCompare(b.descripcion))
            setSectores(filtered)
            setValue('codSectorComercial', null)
        })
    }, [selectedPais])

    // Cargar relaciones cuando cambia el sector
    useEffect(() => {
        if (!selectedSector) {
            setRelaciones([])
            return
        }
        const codSector = selectedSector?.id ?? selectedSector
        HttpLCLSRelacion.getBySector(codSector).then(setRelaciones)
    }, [selectedSector])

    const refetch = () => {
        const codSector = selectedSector?.id ?? selectedSector
        if (codSector) HttpLCLSRelacion.getBySector(codSector).then(setRelaciones)
    }

    const handleRemove = (rel: RelacionViewDTO) => {
        setRelaciones(prev => prev.filter(r => r[RelacionFields.Id] !== rel[RelacionFields.Id]))
    }

    const handleAdd = () => {
        if (!nuevoTipo || !nuevoSubtipo) return

        // Verificar que no exista ya
        const exists = relaciones.some(
            r => r[RelacionFields.CodClienteTipo] === nuevoTipo.id &&
                 r[RelacionFields.CodClienteSubTipo] === nuevoSubtipo.id
        )
        if (exists) {
            toast.error('La relación ya existe')
            return
        }

        const codSector = selectedSector?.id ?? selectedSector
        setRelaciones(prev => [
            ...prev,
            {
                [RelacionFields.Id]: -(prev.length + 1), // temporal negative id
                [RelacionFields.CodSectorComercial]: codSector,
                [RelacionFields.NombreSectorComercial]: '',
                [RelacionFields.CodClienteTipo]: nuevoTipo.id,
                [RelacionFields.DescripcionClienteTipo]: nuevoTipo[ClienteTipoFields.Descripcion],
                [RelacionFields.CodClienteSubTipo]: nuevoSubtipo.id,
                [RelacionFields.DescripcionClienteSubTipo]: nuevoSubtipo[ClienteSubTipoFields.Descripcion],
                [RelacionFields.Activo]: true
            }
        ])
        setNuevoTipo(null)
        setNuevoSubtipo(null)
    }

    const handleSave = () => {
        const codSector = selectedSector?.id ?? selectedSector
        if (!codSector) return Promise.resolve()

        const items: RelacionItemDTO[] = relaciones.map(r => ({
            codClienteTipo: r[RelacionFields.CodClienteTipo],
            codClienteSubTipo: r[RelacionFields.CodClienteSubTipo]
        }))

        const promise = HttpLCLSRelacion.update(codSector, items)
            .then(() => refetch())

        toast.promise(promise, {
            success: 'Relaciones actualizadas',
            error: 'Error al guardar',
            loading: 'Guardando'
        })

        return promise
    }

    const columnas: GridColDef<RelacionViewDTO>[] = [
        { headerName: 'Tipo de Cliente', field: RelacionFields.DescripcionClienteTipo, flex: 2 },
        { headerName: 'Subtipo de Cliente', field: RelacionFields.DescripcionClienteSubTipo, flex: 2 },
        {
            field: 'actions', type: 'actions', width: 60,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={<Tooltip title='Quitar'><RemoveCircleOutline fontSize='small' /></Tooltip>}
                    label='Quitar' onClick={() => handleRemove(row)} color='error'
                />
            ]
        }
    ]

    const codSector = selectedSector?.id ?? selectedSector

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Configuración de Relaciones LCLS'
                    subheader='Sector Comercial - Tipo Cliente - Subtipo Cliente'
                    action={
                        <Stack direction='row' spacing={1}>
                            {codSector && (
                                <>
                                    <IconButton onClick={refetch}><CachedIcon /></IconButton>
                                    <Button variant='contained' color='primary' promise={handleSave}>
                                        Guardar
                                    </Button>
                                </>
                            )}
                        </Stack>
                    }
                />

                {/* Filtros cascada */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <Controller
                            control={control}
                            name='codPais'
                            render={({ field }) => (
                                <Autocomplete
                                    options={paisLogos.map(s => ({ id: s.id, descripcion: s[EntityWithIdAndDescriptionFields.Description] }))}
                                    value={field.value}
                                    onChange={(_, v) => field.onChange(v)}
                                    getOptionLabel={(o: any) => o.descripcion ?? ''}
                                    isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                                    size='small'
                                    renderInput={params => <TextField {...params} label='País *' />}
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <Controller
                            control={control}
                            name='codSectorComercial'
                            render={({ field }) => (
                                <Autocomplete
                                    disabled={!selectedPais}
                                    options={sectores}
                                    value={field.value}
                                    onChange={(_, v) => field.onChange(v)}
                                    getOptionLabel={(o: any) => o.descripcion ?? ''}
                                    isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                                    size='small'
                                    renderInput={params => <TextField {...params} label='Sector Comercial *' />}
                                />
                            )}
                        />
                    </Box>
                </Box>

                {/* Agregar nueva relación */}
                {codSector && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                        <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                            Agregar:
                        </Typography>
                        <Autocomplete
                            options={tipos}
                            value={nuevoTipo}
                            onChange={(_, v) => setNuevoTipo(v)}
                            getOptionLabel={(o) => o[ClienteTipoFields.Descripcion]}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            size='small'
                            sx={{ flex: 1 }}
                            renderInput={params => <TextField {...params} label='Tipo de Cliente' />}
                        />
                        <Autocomplete
                            options={subtipos}
                            value={nuevoSubtipo}
                            onChange={(_, v) => setNuevoSubtipo(v)}
                            getOptionLabel={(o) => o[ClienteSubTipoFields.Descripcion]}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            size='small'
                            sx={{ flex: 1 }}
                            renderInput={params => <TextField {...params} label='Subtipo de Cliente' />}
                        />
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            onClick={handleAdd}
                            disabled={!nuevoTipo || !nuevoSubtipo}
                        >
                            Agregar
                        </Button>
                    </Box>
                )}

                {/* Grilla */}
                {codSector ? (
                    <Box sx={{ mx: 2, mb: 2 }}>
                        <DataGrid
                            disableColumnFilter
                            disableColumnMenu
                            columns={columnas}
                            rows={relaciones}
                            getRowId={row => row[RelacionFields.Id]}
                            density='compact'
                            pagination
                            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                            pageSizeOptions={[25, 50, 75, 100]}
                        />
                    </Box>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color='text.secondary'>
                            Seleccione un país y sector comercial para ver las relaciones
                        </Typography>
                    </Box>
                )}
            </Card>
        </WrapperTable>
    )
}

export default ConfigRelaciones
